import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import Breadcrumbs from "../../../common/BreadCrum/BreadCrum";
import PageHeadingSection from "../../../common/pageheading/PageHeadingSection";
import ToggleSwitch from "../../../common/formfields/ToggleSwitch";
import SlotCard from "./components/SlotCard";
import StackGroup from "./components/StackGroup";
import ParkingDetailsPanel from "./components/ParkingDetailsPanel";
import GenerateSlotsModal from "./components/GenerateSlotsModal";
import AllocateParkingModal from "./components/AllocateParkingModal";
import AssignParkingModal from "./components/AssignParkingModal";
import AssignedParkingList from "./assignedparking/AssignedParkingList";
import apiServices from "../../../../services/apiService";
import storageService from "../../../../utils/storageService";
import { toast } from "react-toastify";

const ParkingOperations = () => {
    const [slotsData, setSlotsData] = useState([]);
    const [parkingTypeOptions, setParkingTypeOptions] = useState([]);
    const [wingOptions, setWingOptions] = useState([]);
    const [allocationTypeOptions, setAllocationTypeOptions] = useState([]);
    const [unitOptions, setUnitOptions] = useState([]);
    const [showStandard, setShowStandard] = useState(true);
    const [showStack, setShowStack] = useState(true);

    const [selectedSlots, setSelectedSlots] = useState([]);
    const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
    const [isAllocateModalOpen, setIsAllocateModalOpen] = useState(false);
    const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
    const [parkingFloors, setParkingFloors] = useState([]);
    const [buildingFloors, setBuildingFloors] = useState([]);

    const { user: authUser } = useSelector((state) => state.auth);
    const siteId = authUser?.siteId || authUser?.SiteId || storageService.getItem("siteId");
    const unitId = authUser?.unitId || authUser?.UnitId || authUser?.branchorunitID || authUser?.BranchorunitID || storageService.getItem("unitId") || storageService.getItem("branchorunitID");
    const roleId = Number(authUser?.roleId || authUser?.RoleId || storageService.getItem("roleId"));
    const isUnitAdmin = roleId === 4 || roleId === 6;
    const [viewMode, setViewMode] = useState(isUnitAdmin ? "list" : "grid");

    const fetchSlots = async () => {
        if (!siteId && !unitId) return;
        try {
            const url = isUnitAdmin
                ? `/ParkingSlotMaster/unit/${unitId}`
                : `/ParkingSlotMaster/site/${siteId}`;

            const response = await apiServices({ method: 'GET', url });
            if (response.success && response.data) {
                let apiData = response.data.Data;
                const formatted = apiData.map(s => {
                    const sysGroup = s.SystemGroupId;
                    const slotNum = s.SlotNumber;
                    const isStack = !!sysGroup;
                    let level = s.levelName || s.LevelName || null;

                    if (isStack && !level && slotNum) {
                        const parts = slotNum.split('-');
                        if (parts.length > 0) {
                            level = parts[parts.length - 1]; // e.g. "Middle"
                        }
                    }

                    return {
                        id: s.id || s.Id,
                        slotNumber: slotNum,
                        type: isStack ? 'Stack' : 'Standard',
                        parkingTypeName: s.parkingTypeName || s.ParkingTypeName || null,
                        floorName: s.floorName || s.FloorName,
                        status: s.status || s.Status || (s.isActive === false || s.IsActive === false ? 'Inactive' : 'Available'),
                        allocationType: s.allocationTypeName || s.AllocationTypeName || 'General',
                        allocatedUnitId: s.allocatedUnitId || s.AllocatedUnitId || null,
                        allocatedUnitName: s.allocatedUnitName || s.AllocatedUnitName || null,
                        originalOwnerUnitId: s.originalOwnerUnitId || s.OriginalOwnerUnitId || null,
                        currentRenterUnitId: s.currentRenterUnitId || s.CurrentRenterUnitId || null,
                        originalOwnerUnitName: s.originalOwnerUnitName || s.OriginalOwnerUnitName || null,
                        currentRenterUnitName: s.currentRenterUnitName || s.CurrentRenterUnitName || null,
                        rentalStatus: s.rentalStatus || s.RentalStatus || null,
                        hasChargingPoint: s.hasChargingPoint || s.HasChargingPoint || false,
                        pricingPerHour: s.pricingPerHour || s.PricingPerHour || null,
                        lastUpdated: 'Just now',
                        groupName: sysGroup || s.groupName || s.GroupName || null,
                        levelName: level,
                    };
                });
                setSlotsData(formatted);
            }
        } catch (error) {
            console.error("Error fetching slots", error);
        }
    };

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                if (siteId) {
                    const resParkingTypeMaster = await apiServices({ method: 'GET', url: `/ParkingTypeMaster/site/${siteId}` });
                    if (resParkingTypeMaster.success && resParkingTypeMaster.data) {
                        let apiData = resParkingTypeMaster.data.Data;
                        setParkingTypeOptions(apiData.map(t => ({ label: t.TypeName, value: t.Id, original: t })));
                    }
                }

                const floorRes = await apiServices({ method: 'GET', url: `/FloorMaster/GetAllFloor` });
                if (floorRes.success && floorRes.data) {
                    let apiData = floorRes.data.FloorMasterData || [];
                    const siteFloors = apiData.filter(f => !siteId || f.SiteId == siteId || f.siteId == siteId);
                    setBuildingFloors(siteFloors.map(f => ({
                        label: f.FloorName || f.floorName,
                        value: f.Id || f.id || f.floorId,
                        wingId: f.WingId || f.wingId,
                        original: f
                    })));
                    const filteredFloors = siteFloors.filter(f =>
                        f.FloorName?.toLowerCase().includes('parking') ||
                        f.FloorName?.toLowerCase().includes('basement') ||
                        f.FloorType === 'Parking' ||
                        f.IsParkingFloor === true ||
                        f.floorName?.toLowerCase().includes('parking') ||
                        f.floorName?.toLowerCase().includes('basement') ||
                        f.floorType === 'Parking' ||
                        f.isParkingFloor === true
                    );
                    setParkingFloors(filteredFloors.map(f => ({ label: f.FloorName || f.floorName, value: f.Id || f.id || f.floorId, original: f })));
                }

                const wingRes = await apiServices({ method: 'GET', url: '/WingMaster/GetAllWing' });
                if (wingRes.success && wingRes.data) {
                    let apiData = wingRes.data.WingMasterData;
                    const filteredWings = apiData.filter(w => w.SiteId == siteId);
                    setWingOptions(filteredWings.map(w => ({ label: w.WingName, value: w.Id, original: w })));
                }

                const allocRes = await apiServices({ method: 'GET', url: `/AllocationTypeMaster/site/${siteId}` });
                if (allocRes.success && allocRes.data) {
                    let apiData = allocRes.data.Data;
                    setAllocationTypeOptions(apiData.map(a => ({
                        label: a.AllocationTypeName || a.allocationTypeName || a.TypeName || a.typeName,
                        value: a.Id || a.id || a.allocationTypeId,
                        original: a
                    })));
                }

                const unitRes = await apiServices({ method: 'GET', url: `/UnitMaster/GetAllUnitMaster?siteId=${siteId || ''}&pageNumber=1&pageSize=10000` });
                if (unitRes.success && unitRes.data) {
                    let apiData = unitRes.data.data;
                    setUnitOptions(apiData.map(u => ({
                        label: `${u.UnitName || u.unitName || ''} (${u.UnitNumber || u.unitNumber || u.UnitNo || u.unitNo || ''})`,
                        value: u.UnitId || u.Id || u.id || u.unitId,
                        original: u
                    })));
                }
            } catch (error) {
                console.error("Error fetching parking master data:", error);
            }
        };
        fetchMasterData();
        fetchSlots();
    }, [siteId, unitId, roleId]);

    // Derived Data
    const filteredSlots = useMemo(() => {
        let filtered = slotsData;

        // Type toggle filter
        if (!showStandard) filtered = filtered.filter(s => s.type !== 'Standard');
        if (!showStack) filtered = filtered.filter(s => s.type !== 'Stack');

        return filtered;
    }, [slotsData, showStandard, showStack]);

    const standardSlots = filteredSlots.filter(s => s.type === 'Standard');
    const stackSlots = filteredSlots.filter(s => s.type === 'Stack');

    // Group stack slots
    const stackedGroups = stackSlots.reduce((acc, slot) => {
        if (!acc[slot.groupName]) acc[slot.groupName] = [];
        acc[slot.groupName].push(slot);
        return acc;
    }, {});

    const handleAllocate = (slots) => {
        if (isUnitAdmin) {
            setIsAssignModalOpen(true);
        } else {
            setIsAllocateModalOpen(true);
        }
    };

    const handleConfirmAllocation = async (data) => {
        const { allocationTypeId, allocatedUnitId, slots } = data;
        const slotIds = slots.map(s => s.id);

        const payload = {
            slotIds: slotIds,
            allocationTypeId: allocationTypeId,
            allocatedUnitId: allocatedUnitId || null,
            status: "Allocated",
            updatedBy: authUser?.name || "Admin"
        };

        try {
            const res = await apiServices({
                method: "POST",
                url: "/ParkingSlotMaster/bulk-allocate",
                data: payload
            });

            if (res?.data?.isSuccess || res?.data?.IsSuccess) {
                fetchSlots();
                setSelectedSlots([]);
            } else {
                console.error(res?.data?.message || res?.data?.Message || "Failed to allocate slots");
            }
        } catch (error) {
            console.error("Error calling bulk-allocate", error);
        }
    };

    const handleConfirmAssignment = async (data) => {
        const { vehicleNumber, vehicleType, mobileNumber, startDate, endDate, slots, assignedUser, assignedUserId } = data;

        const slot = slots[0];
        if (!slot) return;

        const effectiveUnitId = unitId || (slot.rentalStatus === 'Rented' ? (slot.currentRenterUnitId || slot.CurrentRenterUnitId) : (slot.allocatedUnitId || slot.AllocatedUnitId)) || 0;

        const payload = {
            id: 0,
            slotId: slot.id,
            unitId: effectiveUnitId,
            assignedUserId: assignedUserId || 0,
            vehicleNumber: vehicleNumber || "",
            vehicleType: vehicleType || "",
            mobileNumber: mobileNumber || "",
            startDate: startDate ? (startDate.includes('T') ? startDate : `${startDate}T00:00:00`) : new Date().toISOString(),
            endDate: endDate ? (endDate.includes('T') ? endDate : `${endDate}T23:59:59`) : null,
            createdBy: authUser?.name || authUser?.userName || "Admin"
        };

        try {
            const res = await apiServices({
                method: "POST",
                url: "/ParkingAssignment",
                data: payload
            });

            if (res?.data?.isSuccess || res?.data?.IsSuccess || res?.success || res?.isSuccess) {
                toast.success(res?.data?.message || res?.data?.Message || "Parking bay assigned successfully!");
                fetchSlots();
                setSelectedSlots([]);
                setIsAssignModalOpen(false);
            } else {
                toast.error(res?.data?.message || res?.data?.Message || "Failed to assign parking slot");
            }
        } catch (error) {
            console.error("Error calling ParkingAssignment", error);
            toast.error(error?.response?.data?.message || "Error calling ParkingAssignment");
        }
    };

    const saved = (res) => res?.data?.isSuccess === true || res?.data?.IsSuccess === true;

    const handleRelease = async (slotsToRelease) => {
        const slots = Array.isArray(slotsToRelease) ? slotsToRelease : [slotsToRelease];
        for (const slot of slots) {
            try {
                if (isUnitAdmin || slot.status?.toLowerCase() === "occupied") {
                    const assignRes = await apiServices({
                        method: "GET",
                        url: `/ParkingAssignment?slotId=${slot.id}`
                    });
                    const assignments = assignRes?.data?.Data || assignRes?.Data || [];
                    if (assignments.length > 0) {
                        const assignmentId = assignments[0].Id || assignments[0].id;
                        const revokeRes = await apiServices({
                            method: "DELETE",
                            url: `/ParkingAssignment/${assignmentId}`
                        });
                        if (saved(revokeRes)) {
                            toast.success("Parking assignment revoked successfully");
                        } else {
                            toast.error(revokeRes?.data?.message || "Failed to revoke assignment");
                        }
                        continue;
                    }
                }

                const res = await apiServices({
                    method: "PATCH",
                    url: `/ParkingSlotMaster/${slot.id}/release-allocation`
                });
                if (!saved(res)) {
                    toast.error(res?.data?.message || res?.data?.Message || "Failed to release slot");
                    return;
                } else {
                    toast.success("Slot allocation released successfully");
                }
            } catch (err) {
                console.error("Error releasing slot", err);
                toast.error("Error releasing slot");
            }
        }
        fetchSlots();
        setSelectedSlots([]);
    };

    const toggleSlotSelection = (slot) => {
        const isSelected = selectedSlots.some(s => s.id === slot.id);
        if (isSelected) {
            setSelectedSlots(prev => prev.filter(s => s.id !== slot.id));
        } else {
            setSelectedSlots(prev => [...prev, slot]);
        }
    };

    const toggleStackGroupSelection = (groupName) => {
        const stackGroupSlots = slotsData.filter(s => s.groupName === groupName);
        const allSelected = stackGroupSlots.every(s => selectedSlots.some(selected => selected.id === s.id));

        if (allSelected) {
            // Deselect all slots in group
            setSelectedSlots(prev => prev.filter(s => s.groupName !== groupName));
        } else {
            // Select all slots in group
            setSelectedSlots(prev => {
                const newSlots = prev.filter(s => s.groupName !== groupName);
                return [...newSlots, ...stackGroupSlots];
            });
        }
    };

    const handleGenerate = async (data) => {
        const { floor, parkingTypeId, quantity } = data;
        const floorIdVal = floor?.value || floor;
        const typeIdVal = parkingTypeId?.value || parkingTypeId;

        const selectedFloor = parkingFloors.find(f => f.value === floorIdVal);
        const selectedType = parkingTypeOptions.find(p => p.value === typeIdVal);

        const payload = {
            siteId: siteId,
            floorId: selectedFloor ? selectedFloor.original.Id : 0,
            floorName: selectedFloor ? selectedFloor.original.FloorName : "",
            wingId: selectedFloor ? selectedFloor.original.WingId : 0,
            parkingTypeId: selectedType ? selectedType.original.Id : 0,
            standardSlotQuantity: selectedType?.original?.StructureType?.toLowerCase() !== "stack" ? quantity : 0,
            stackSystemQuantity: selectedType?.original?.StructureType?.toLowerCase() === "stack" ? quantity : 0,
            createdBy: authUser?.name || "Admin"
        };

        try {
            const res = await apiServices({
                method: "POST",
                url: "/ParkingSlotMaster/bulk-generate",
                data: payload
            });
            if (res?.data?.isSuccess || res?.data?.IsSuccess) {
                fetchSlots();
                setIsGenerateModalOpen(false);
            } else {
                console.error(res?.data?.message || res?.data?.Message || "Failed to generate slots");
            }
        } catch (error) {
            console.error("Bulk generate error", error);
        }
    };

    if (viewMode === "list") {
        return <AssignedParkingList onSwitchToGrid={() => setViewMode("grid")} />;
    }

    return (
        <div className="bg-dashboardbg px-4 sm:px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto h-full relative">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading="Interactive Visual Floor Layout"
                subHeading="Real-time floor grid with distinct Standard and Stack parking structural views."
                primaryBtnText={(roleId !== 4 && roleId !== 6) ? "Generate Slots" : "Back to List"}
                primaryBtnIcon={(roleId !== 4 && roleId !== 6) ? faPlus : undefined}
                PrimaryOnClick={(roleId !== 4 && roleId !== 6) ? () => setIsGenerateModalOpen(true) : () => setViewMode("list")}
                seconadaryBtnText={(roleId !== 4 && roleId !== 6) ? "Back to List" : undefined}
                SecandaryOnClick={(roleId !== 4 && roleId !== 6) ? () => setViewMode("list") : undefined}
            />

            {/* Controls & Count */}
            <div className="flex flex-col sm:flex-row items-center justify-between mt-4 shrink-0">
                <h3 className="text-[10px] font-bold text-gray-500 dark:text-textsecondary tracking-widest uppercase mb-4 sm:mb-0">
                    Showing {filteredSlots.length} of {slotsData.length} slots
                </h3>
                <div className="flex items-center gap-6">
                    <ToggleSwitch label="Show Standard" checked={showStandard} onChange={setShowStandard} />
                    <ToggleSwitch label="Show Stack" checked={showStack} onChange={setShowStack} />
                </div>
            </div>

            {/* Grid Container */}
            <div className="flex-1 mt-6 flex gap-6 pb-20">
                <div className="flex-1">
                    {filteredSlots.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-textsecondary">
                            <span className="material-symbols-outlined text-5xl mb-3">grid_off</span>
                            <p className="font-semibold text-sm">No slots found matching criteria</p>
                        </div>
                    ) : (
                        <div className="flex flex-col gap-8">
                            {/* Assuming grouped by floor in a real scenario, here we just show B2 */}
                            <div>
                                <div className="flex items-end gap-3 mb-3">
                                    <h2 className="text-base font-bold text-gray-800 dark:text-textprimary">{filteredSlots[0]?.floorName || "Parking"}</h2>
                                    <span className="text-xs text-gray-500 dark:text-textsecondary mb-0.5">
                                        {filteredSlots.length} slots • {filteredSlots.filter(s => isUnitAdmin ? (s.status?.toLowerCase() !== 'occupied' && !(s.rentalStatus === 'Rented' && s.originalOwnerUnitId == unitId && s.currentRenterUnitId != unitId)) : (s.status?.toLowerCase() === 'available')).length} available
                                    </span>
                                </div>

                                <div className="flex flex-wrap gap-3">
                                    {standardSlots.map(slot => (
                                        <SlotCard
                                            key={slot.id}
                                            slot={slot}
                                            isSelected={selectedSlots.some(s => s.id === slot.id)}
                                            onClick={toggleSlotSelection}
                                            isUnitAdmin={isUnitAdmin}
                                            unitId={unitId}
                                        />
                                    ))}
                                </div>

                                {Object.keys(stackedGroups).length > 0 && (
                                    <div className="flex flex-wrap gap-4 mt-8">
                                        {Object.entries(stackedGroups).map(([groupName, slots]) => (
                                            <StackGroup
                                                key={groupName}
                                                groupName={groupName}
                                                slots={slots}
                                                selectedSlotIds={selectedSlots.map(s => s.id)}
                                                onSlotClick={toggleSlotSelection}
                                                onHeaderClick={() => toggleStackGroupSelection(groupName)}
                                                isUnitAdmin={isUnitAdmin}
                                                unitId={unitId}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Details Panel - Rendered dynamically if selectedSlots exists */}
                {selectedSlots.length > 0 && (
                    <div className="hidden lg:block w-[280px] shrink-0 transition-all duration-300">
                        <div className="sticky top-0 h-[calc(100vh-200px)]">
                            <ParkingDetailsPanel
                                slots={selectedSlots}
                                onClose={() => setSelectedSlots([])}
                                onAllocate={() => handleAllocate(selectedSlots)}
                                onRelease={() => handleRelease(selectedSlots)}
                                roleId={roleId}
                                unitId={unitId}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile/Tablet Overlay Details Panel */}
            {selectedSlots.length > 0 && (
                <div className="lg:hidden fixed inset-0 z-50">
                    <div className="absolute inset-0 bg-black/30" onClick={() => setSelectedSlots([])}></div>
                    <ParkingDetailsPanel
                        slots={selectedSlots}
                        onClose={() => setSelectedSlots([])}
                        onAllocate={() => handleAllocate(selectedSlots)}
                        onRelease={() => handleRelease(selectedSlots)}
                        roleId={roleId}
                        unitId={unitId}
                    />
                </div>
            )}

            <GenerateSlotsModal
                isOpen={isGenerateModalOpen}
                onClose={() => setIsGenerateModalOpen(false)}
                onGenerate={handleGenerate}
                floorOptions={parkingFloors}
                wingOptions={wingOptions}
                parkingTypeOptions={parkingTypeOptions}
            />

            <AllocateParkingModal
                isOpen={isAllocateModalOpen}
                onClose={() => setIsAllocateModalOpen(false)}
                onConfirm={handleConfirmAllocation}
                selectedSlots={selectedSlots}
                allocationTypeOptions={allocationTypeOptions}
                wingOptions={wingOptions}
                floorOptions={buildingFloors}
                unitOptions={unitOptions}
                siteId={siteId}
            />

            <AssignParkingModal
                isOpen={isAssignModalOpen}
                onClose={() => setIsAssignModalOpen(false)}
                onConfirm={handleConfirmAssignment}
                selectedSlots={selectedSlots}
                unitId={unitId || selectedSlots[0]?.allocatedUnitId}
            />
        </div>
    );
};

export default ParkingOperations;
